import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, messageErreur } from "@/lib/api";
import { initialesDe } from "@/components/messagerie/Wa";

const duree = (s) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return h ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`; };
const estNouveau = (f) => f.created_at && Date.now() - new Date(f.created_at) < 86400000;
const Nouveau = () => <span className="text-[10px] font-semibold px-1.5 py-0.5 at-pill" style={{ background: "var(--badge, var(--accent))", color: "var(--sur-badge, var(--accent-ink))" }}>nouveau</span>;

const Barre = ({ repartition, statuts }) => {
  const total = Object.values(repartition || {}).reduce((a, b) => a + b, 0);
  return (
    <div className="flex h-1.5 at-pill overflow-hidden gap-px" style={{ background: "var(--surface-3, var(--surface-2))" }}>
      {statuts.filter((s) => repartition?.[s.code]).map((s) => <span key={s.code} style={{ flex: repartition[s.code] / Math.max(1, total), background: s.couleur }} />)}
    </div>
  );
};

const ChoixFichier = ({ fichiers, choix, setChoix, restants }) => {
  const [ouvert, setOuvert] = useState(false);
  const f = fichiers.find((x) => x.id === choix);
  return (
    <Popover open={ouvert} onOpenChange={setOuvert}>
      <PopoverTrigger asChild>
        <button type="button" className="mq-bloc w-full flex items-center gap-3 text-left !py-2.5" data-testid="calls-file-picker">
          <span className="mq-icone">📂</span>
          <span className="flex-1 min-w-0"><span className="block text-[11px] at-muted">Fichier de prospection</span><span className="block text-[15px] truncate">{f ? f.nom : "Choisir un fichier"}</span></span>
          <span className="text-xs px-2 py-0.5 at-pill shrink-0" style={{ background: "var(--chip-actif-fond, var(--surface-2))", color: "var(--chip-actif-texte, var(--ink))" }}>{f ? f.restants : restants} restants</span>
          <ChevronDown size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="at-popover w-80 p-2" data-testid="calls-file-menu">
        <button type="button" onClick={() => { setChoix(null); setOuvert(false); }} className="w-full min-h-[44px] px-3 text-left text-sm rounded-xl hover:bg-[var(--surface-2)]">Tous mes fichiers</button>
        {fichiers.map((x) => <button key={x.id} type="button" onClick={() => { setChoix(x.id); setOuvert(false); }} className="w-full min-h-[44px] px-3 text-left text-sm rounded-xl hover:bg-[var(--surface-2)]" data-testid={`calls-file-option-${x.id}`}>{x.nom} · {x.restants} rest.</button>)}
      </PopoverContent>
    </Popover>
  );
};

const CarteFichier = ({ f, statuts, mise }) => (
  <div className="mq-bloc space-y-3" style={mise ? { background: "linear-gradient(var(--selection, var(--surface-2)), var(--surface) 40%)" } : undefined} data-testid={`calls-file-card-${f.id}`}>
    <div className="flex items-start gap-3"><span className="mq-icone">📂</span><div className="min-w-0 flex-1"><p className="text-[15px] truncate">{f.nom}</p><p className="text-xs at-muted">{f.operation || "Fichier de prospection"}</p></div>{estNouveau(f) && <Nouveau />}</div>
    <div className="grid grid-cols-3 gap-2">
      <div><p className="text-[22px] tabular-nums">{f.restants}</p><p className="text-xs at-muted">à appeler</p></div>
      <div><p className="text-[22px] tabular-nums">{f.total ? Math.round((100 * (f.total - f.restants)) / f.total) : 0} %</p><p className="text-xs at-muted">traité</p></div>
      <div><p className="text-[22px] tabular-nums" style={{ color: "var(--primaire-texte, var(--accent))" }}>{f.rdv}</p><p className="text-xs at-muted">RDV</p></div>
    </div>
    <Barre repartition={f.repartition} statuts={statuts} />
    <Link to="/focus" className="mq-avancer !h-[34px] !px-4" data-testid={`calls-file-start-${f.id}`}><Play size={13} fill="currentColor" />Démarrer ce fichier</Link>
  </div>
);

const Session = ({ d }) => {
  const c = d.compteur;
  const debut = d.derniers_appels?.filter((a) => new Date(a.created_at).toDateString() === new Date().toDateString()).at(-1)?.created_at;
  return (
    <div className="mq-bloc" data-testid="calls-session">
      <div className="flex justify-between text-sm"><span>⚡ Ma session</span><span className="text-xs at-muted">{debut ? `depuis ${new Date(debut).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "pas encore commencée"}</span></div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        {[[c.appels, "appels"], [c.decroches, "joints"], [c.opportunites, c.opportunites > 1 ? "opportunités" : "opportunité"], [duree(d.temps_ligne_s), "en ligne"]].map(([v, l]) => <div key={l} className="mq-tuile"><p className="text-xl tabular-nums">{v}</p><p className="text-xs at-muted">{l}</p></div>)}
      </div>
      <div className="flex justify-between text-xs mt-3"><span className="at-muted">Objectif du jour</span><span className="tabular-nums">{c.appels} / {d.objectifs.objectif_appels}</span></div>
      <div className="h-1.5 at-pill mt-1.5 overflow-hidden" style={{ background: "var(--surface-3, var(--surface-2))" }}><span className="block h-full" style={{ width: `${Math.min(100, (100 * c.appels) / d.objectifs.objectif_appels)}%`, background: "var(--primaire, var(--accent))" }} /></div>
    </div>
  );
};

const Resultats = ({ statuts }) => {
  const total = statuts.reduce((s, x) => s + x.nombre, 0);
  return (
    <div className="mq-bloc" data-testid="calls-results">
      <div className="flex justify-between text-sm"><span>📊 Mes résultats</span><span className="text-xs at-muted">{total} contacts</span></div>
      <div className="flex h-3 gap-0.5 mt-3">{statuts.filter((s) => s.nombre).map((s) => <span key={s.code} className="rounded-sm" style={{ flex: s.nombre, background: s.couleur }} />)}</div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-[13px]">{statuts.map((s) => <span key={s.code} className="flex items-center gap-2"><span className="w-2 h-2 at-pill" style={{ background: s.couleur }} /><span className="flex-1 truncate">{s.nom}</span><b className="font-normal tabular-nums">{s.nombre}</b></span>)}</div>
    </div>
  );
};

export default function MesAppels() {
  const [d, setD] = useState(null);
  const [liste, setListe] = useState([]);
  const [choix, setChoix] = useState(null);
  const [filtre, setFiltre] = useState("a_appeler");
  useEffect(() => {
    api.get("/poste/tableau").then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e)));
    api.get("/appels/ma-liste").then((r) => setListe(r.data.a_appeler)).catch(() => {});
  }, []);
  const contacts = useMemo(() => liste.filter((p) => (!choix || p.fichier_id === choix) && (filtre === "a_appeler" || p.statut_appel === filtre)), [liste, choix, filtre]);
  if (!d) return <p className="at-muted" data-testid="calls-loading">Chargement…</p>;
  const fichiers = d.fichiers;
  const ensuite = [...d.manques.map((m) => ({ id: m.id, nom: m.nom, sous: `Appel manqué à ${new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "numeric", minute: "2-digit" }).replace(":", " h ")}`, pid: m.prospect_id })),
    ...d.suivants.map((p) => ({ id: p.id, nom: `${p.prenom} ${p.nom}`, sous: p.tentatives ? `${p.statut_nom} · tentative ${p.tentatives + 1}` : "Jamais appelé · fichier frais", pid: p.id }))].slice(0, 3);
  return (
    <div className="mq-cols appels" data-testid="page-calls">
      <section className="mq-panneau">
        <div className="mq-tete"><h2 className="flex-1">Ma liste d'appels</h2><span className="text-xs opacity-90" data-testid="calls-count">{d.a_appeler} à appeler</span></div>
        <div className="mq-corps space-y-3">
          <ChoixFichier fichiers={fichiers} choix={choix} setChoix={setChoix} restants={d.a_appeler} />
          <div className="mq-chips" data-testid="calls-filters">
            <button type="button" onClick={() => setFiltre("a_appeler")} className={`at-chip ${filtre === "a_appeler" ? "actif" : ""}`} data-testid="calls-filter-a_appeler">À appeler {d.a_appeler}</button>
            {d.statuts.filter((s) => liste.some((p) => p.statut_appel === s.code)).map((s) => <button key={s.code} type="button" onClick={() => setFiltre(s.code)} className={`at-chip ${filtre === s.code ? "actif" : ""}`} data-testid={`calls-filter-${s.code}`}>{s.nom} {liste.filter((p) => p.statut_appel === s.code).length}</button>)}
          </div>
          {!choix && filtre === "a_appeler" ? (
            <>
              <p className="text-[13px] at-muted">Mes fichiers</p>
              {fichiers.map((f) => (
                <button key={f.id} type="button" onClick={() => setChoix(f.id)} className="w-full flex items-center gap-3 min-h-[52px] text-left" data-testid={`calls-file-row-${f.id}`}>
                  <span className="mq-icone">📂</span>
                  <span className="flex-1 min-w-0"><span className="block text-[15px] truncate">{f.nom}</span><span className="block text-xs at-muted truncate">{f.operation || "Fichier de prospection"}</span></span>
                  <span className="flex flex-col items-end gap-1 text-xs at-muted shrink-0">{f.restants} rest.{estNouveau(f) && <Nouveau />}</span>
                </button>
              ))}
            </>
          ) : contacts.map((p) => (
            <Link key={p.id} to={`/focus?prospect=${p.id}`} className="flex items-center gap-3 min-h-[52px]" data-testid={`calls-contact-${p.id}`}>
              <span className="at-avatar text-xs" style={{ width: 36, height: 36, background: "var(--surface-3, var(--surface-2))" }}>{initialesDe(`${p.prenom} ${p.nom}`)}</span>
              <span className="flex-1 min-w-0"><span className="block text-[15px] truncate">{p.prenom} {p.nom}</span><span className="block text-xs at-muted truncate">{p.ville} · {p.statut_nom}</span></span>
              <span className="w-2 h-2 at-pill shrink-0" style={{ background: p.statut_couleur }} />
            </Link>
          ))}
        </div>
      </section>
      <section className="mq-panneau doux">
        <div className="mq-corps !p-6 space-y-4">
          <h2 className="text-[22px] leading-snug">Choisissez votre fichier de prospection</h2>
          <p className="text-[13px] at-texte-2">Vous appelez les contacts d'un fichier à la fois. Les rappels à l'heure et les appels manqués restent toujours prioritaires.</p>
          {fichiers.map((f, i) => <CarteFichier key={f.id} f={f} statuts={d.statuts} mise={i === 0} />)}
        </div>
      </section>
      <section className="mq-panneau doux">
        <div className="mq-corps space-y-3">
          <Session d={d} />
          <Resultats statuts={d.statuts} />
          <div className="mq-bloc" data-testid="calls-next">
            <p className="text-sm">⏭️ Ensuite dans la file</p>
            {ensuite.length === 0 && <p className="text-xs at-muted mt-2">Personne pour l'instant.</p>}
            {ensuite.map((x) => <Link key={x.id} to={`/focus?prospect=${x.pid}`} className="flex items-center gap-3 min-h-[50px] mt-1"><span className="at-avatar text-xs" style={{ width: 34, height: 34, background: "var(--surface-3, var(--surface-2))" }}>{initialesDe(x.nom)}</span><span className="min-w-0"><span className="block text-sm truncate">{x.nom}</span><span className="block text-xs at-muted truncate">{x.sous}</span></span></Link>)}
          </div>
          <div className="mq-bloc flex items-center justify-between gap-2" data-testid="calls-ringover">
            <span className="text-sm">☎️ Ringover</span>
            <span className="text-xs px-3 py-1.5 at-pill flex items-center gap-1.5" style={{ border: "1px solid var(--line)" }}><span className="w-2 h-2 at-pill" style={{ background: "var(--badge, var(--success))" }} />Disponible · simulation</span>
          </div>
        </div>
      </section>
    </div>
  );
}
